/**
 * Durable per-integration auto-resume latch (2026-09-13, decision D4: no auto-resume after a broker
 * kick or a user Disconnect).
 *
 * Each broker relay already keeps an in-memory "do not auto-resume" latch — a rejected logon, a kick
 * or an explicit Disconnect must not be silently retried on the next SignalR blip. Module memory dies
 * with the page, though, so a reload (or a second tab) auto-resumed straight back into the same
 * rejection / kick: the prod 2026-09-12 Rithmic `rp_code 13` loop and the CQG "Too many failed"
 * lockout both ran through that gap. This latch lives in `localStorage`: shared by every tab of this
 * browser and it survives reloads. ONLY an explicit user Connect releases it
 * (`runRithmicConnectSequence` / `runCqgConnectSequence` → `allow*AutoResume`).
 *
 * Every auto-resume path consults it: the dialogs' page-load resume, `*ReconnectNeeded`, the hub's
 * `onreconnected` → `*Resume`, the hub-start `*Resume`, the relay's re-logon on an already-open
 * socket, and (CQG) the dead-pipe retry ladder.
 */
export type BrokerIntegration = 'rithmic' | 'cqg';

/** Why auto-resume is latched off. Recorded so the suppression log lines say which event did it. */
export type AutoResumeLatchReason =
  /** The broker refused the stored credentials (Rithmic rp_code≠0 / Reject; CQG LogonResult≠0). */
  | 'logon-rejected'
  /** The broker ended a live session (Rithmic ForcedLogout; CQG LoggedOff FORCED/REASSIGNED). */
  | 'kicked'
  /** The user pressed Disconnect / Unlink. */
  | 'user-disconnected'
  /** The auto-resume backoff ladder ran out (Rithmic: 5→10→20→40→60 s, then stop). */
  | 'resume-exhausted'
  /**
   * The BE ended a Connecting session that heard NO logon answer (reason `logon_timeout`, 2026-09-25):
   * not a refusal, but still no silent retry. (CQG treats that reason as transport and never latches.)
   */
  | 'logon-timeout';

const REASONS: readonly AutoResumeLatchReason[] = ['logon-rejected', 'kicked', 'user-disconnected', 'resume-exhausted', 'logon-timeout'];

/** The localStorage key for an integration's latch. */
export function autoResumeLatchKey(integration: BrokerIntegration): string {
  return `journal.${integration}.autoResumeLatch`;
}

/** Latch auto-resume off for the integration, durably (all tabs, survives reloads). */
export function latchAutoResume(integration: BrokerIntegration, reason: AutoResumeLatchReason): void {
  try { localStorage.setItem(autoResumeLatchKey(integration), reason); } catch { /* storage unavailable — the in-memory latch still holds for this tab */ }
}

/** Release the durable latch — an explicit user Connect only. */
export function releaseAutoResume(integration: BrokerIntegration): void {
  try { localStorage.removeItem(autoResumeLatchKey(integration)); } catch { /* nothing to release */ }
}

/** The recorded latch reason, or null when auto-resume is allowed (unknown/corrupt values read as null). */
export function readAutoResumeLatch(integration: BrokerIntegration): AutoResumeLatchReason | null {
  try {
    const raw = localStorage.getItem(autoResumeLatchKey(integration));
    return raw !== null && (REASONS as readonly string[]).includes(raw) ? (raw as AutoResumeLatchReason) : null;
  } catch {
    return null;
  }
}

/**
 * The latch reason to record for a BE `error` state push, from its machine `reason`: a kick
 * (`kicked`, or `concurrent_session` — the take-over shape of a kick), a logon that got no answer at all
 * (`logon_timeout`), or a refused logon (bare error).
 */
export function latchReasonForBrokerError(reason: string | null | undefined): AutoResumeLatchReason {
  if (reason === 'kicked' || reason === 'concurrent_session') return 'kicked';
  if (reason === 'logon_timeout') return 'logon-timeout';
  return 'logon-rejected';
}
